import { Company } from 'module/api/company/types';
import { useParams } from '@rocketlane/rocket-router';
import { Document } from 'api/pages';
import { EmailPage } from 'api/pages/types';
import { useGetDocumentFeedbackQuery } from 'queries/feedback';
import { useGetDocumentQuery } from 'queries/pages';
import { useCallback, useEffect } from 'react';
import { useEmailPagesRouteNavigations } from './route-navigations';

interface Props {
  company: Company;
  emailPages: EmailPage[];
  isEmailPagesLoading: boolean;
  projectId?: number;
}
interface Result {
  emailPage?: Document;
  isEmailPageLoading: boolean;
}
export const useCurrentEmailPage = ({
  company,
  emailPages,
  isEmailPagesLoading,
  projectId,
}: Props): Result => {
  const { emailId: emailIdParam, emailPageId: emailPageIdParam } = useParams<{
    emailId?: string;
    emailPageId?: string;
  }>();
  const { navigateToEmail } = useEmailPagesRouteNavigations(projectId);

  // Use emailId if available, otherwise use emailPageIdParam
  const documentIdParam = emailIdParam ?? emailPageIdParam;

  const emailId = Number(documentIdParam);
  const isEmailIdValid = !isNaN(emailId);
  const { data: emailDocument, isLoading: isEmailDocumentLoading } =
    useGetDocumentQuery(isEmailIdValid ? emailId : -1, {
      enabled: isEmailIdValid,
      retry: false,
    });
  const { isLoading: isFeedbackLoading } = useGetDocumentFeedbackQuery(
    'DOCUMENT',
    isEmailIdValid ? emailId.toString() : '-1',
    {
      enabled: isEmailIdValid,
      retry: false,
    },
  );
  const navigateToFirstEmailPage = useCallback(() => {
    if (emailPages?.length > 0) {
      const companyIdForNavigation = projectId ?? company.companyId;

      navigateToEmail(
        companyIdForNavigation,
        emailPages[0]?.associations[0]?.documentId,
      );
    }
  }, [emailPages, navigateToEmail, company.companyId, projectId]);

  useEffect(() => {
    if (isEmailPagesLoading) {
      return;
    }
    if (!isEmailIdValid) {
      navigateToFirstEmailPage();
    }
  }, [isEmailPagesLoading, isEmailIdValid, navigateToFirstEmailPage]);
  useEffect(() => {
    if (isEmailDocumentLoading || isEmailPagesLoading) {
      return;
    }
    if (isEmailIdValid && emailDocument == null) {
      navigateToFirstEmailPage();
    }
  }, [
    isEmailDocumentLoading,
    isEmailIdValid,
    emailDocument,
    navigateToFirstEmailPage,
    isEmailPagesLoading,
  ]);

  return {
    emailPage: emailDocument,
    isEmailPageLoading: isEmailDocumentLoading || isFeedbackLoading,
  };
};
